import {FiduciaryMember, FiduciarySummary, FiduciaryKey} from "../../../EstateFlowchart/model/EstateFlowchartTypes";
import {getMemberNameById} from "../../../EstateFlowchart/utils/DefUtils";
import {MemberGroup} from "../../../ClientManagement/models/InvestorGroupType";

const TOTAL_PAGE_HEIGHT = 850;
const SECTION_HEADER_HEIGHT = 45;
const CATEGORY_HEADER_HEIGHT = 48;

type SplitFiduciarySummaryResult = {
    pages: FiduciarySummary[];
};

export function estimateFiduciaryRowHeight(
    members: FiduciaryMember[],
    memberGroup?: MemberGroup,
    maxWidth: number = 600,
    fontSize: number = 16,
    lineHeight: number = 28
): number {
    const names = members.map(item => {
        const name = memberGroup ? getMemberNameById(memberGroup, item.memberId ?? "") : undefined;
        return (name ?? item.customName) ?? "";
    }).join(", ");

    const avgCharWidth = fontSize * 0.6;
    const charsPerLine = Math.floor(maxWidth / avgCharWidth);

    let numLines = 1;
    let currentLineLength = 0;
    const words = names.split(" ");
    for (const word of words) {
        if (currentLineLength + word.length + (currentLineLength > 0 ? 1 : 0) > charsPerLine) {
            numLines++;
            currentLineLength = word.length;
        } else {
            currentLineLength += word.length + (currentLineLength > 0 ? 1 : 0);
        }
    }

    return lineHeight * numLines;
}

export const calculateFiduciarySectionHeight = (
    fiduciaryList: FiduciaryMember[] | undefined,
    memberGroup?: MemberGroup
): number => {
    if (!fiduciaryList || fiduciaryList.length === 0) return 0;

    const memberOrderGroups: Record<number, FiduciaryMember[]> = {};
    fiduciaryList.forEach(member => {
        const order = member.memberOrder ?? 0;
        if (!memberOrderGroups[order]) memberOrderGroups[order] = [];
        memberOrderGroups[order].push(member);
    });

    let totalRowsHeight = 0;
    Object.values(memberOrderGroups).forEach(group => {
        totalRowsHeight += estimateFiduciaryRowHeight(group, memberGroup);
    });

    return CATEGORY_HEADER_HEIGHT + totalRowsHeight;
};

function blankMember(member: FiduciarySummary["primaryMember"] | undefined) {
    return member
        ? {
            ...member,
            executors: [],
            guardiansForMinors: [],
            trustees: [],
            financialPowerOfAttorneys: [],
            healthcarePowerOfAttorneys: []
        }
        : undefined;
}

function hasAnyFiduciaries(member?: FiduciarySummary["primaryMember"]): boolean {
    if (!member) return false;
    return (
        (member.executors?.length ?? 0) > 0 ||
        (member.guardiansForMinors?.length ?? 0) > 0 ||
        (member.trustees?.length ?? 0) > 0 ||
        (member.financialPowerOfAttorneys?.length ?? 0) > 0 ||
        (member.healthcarePowerOfAttorneys?.length ?? 0) > 0
    );
}

function pushPage(page: FiduciarySummary, pages: FiduciarySummary[]) {
    if (
        hasAnyFiduciaries(page.primaryMember) ||
        hasAnyFiduciaries(page.partnerMember) ||
        pages.length === 0
    ) {
        pages.push(page);
    }
}

const FIDUCIARY_FIELDS: FiduciaryKey[] = [
    FiduciaryKey.Executors,
    FiduciaryKey.GuardiansForMinors,
    FiduciaryKey.Trustees,
    FiduciaryKey.FinancialPowerOfAttorneys,
    FiduciaryKey.HealthcarePowerOfAttorneys
];

function groupMembersByOrder(members: FiduciaryMember[]): FiduciaryMember[][] {
    const groupMap: Record<number, FiduciaryMember[]> = {};
    members.forEach(member => {
        const order = member.memberOrder ?? 0;
        if (!groupMap[order]) groupMap[order] = [];
        groupMap[order].push(member);
    });
    return Object.values(groupMap);
}

function assignMemberOrderOverride(groups: FiduciaryMember[][], start: number): FiduciaryMember[] {
    let overrideIndex = start;
    let allWithOverride: FiduciaryMember[] = [];
    groups.forEach(group => {
        const withOverride = group.map(m => ({
            ...m,
            memberOrderOverride: overrideIndex
        }));
        allWithOverride = allWithOverride.concat(withOverride);
        overrideIndex++;
    });
    return allWithOverride;
}

type ProcessFieldContext = {
    memberType: "primaryMember" | "partnerMember",
    blankMemberRef: FiduciarySummary["primaryMember"] | undefined,
    familyTree: MemberGroup | undefined,
    page: FiduciarySummary,
    memberPages: FiduciarySummary[],
    pageHeightRef: { value: number },
    continuedSectionsRef: { value: FiduciaryKey[] },
    memberOrderStartRef: { value: number }
};

function processField(
    field: FiduciaryKey,
    fiduciaryList: FiduciaryMember[] | undefined,
    ctx: ProcessFieldContext
) {
    const sectionHeight = calculateFiduciarySectionHeight(fiduciaryList, ctx.familyTree);
    let remainingList = fiduciaryList || [];
    let pendingContinuedSections: FiduciaryKey[] = [];

    function setFieldOnPage(list: FiduciaryMember[], memberOrderOverrides: number[]) {
        const currentList = list.map((m, idx) => ({
            ...m,
            memberOrderOverride: memberOrderOverrides[idx] ?? m.memberOrderOverride
        }));
        if (ctx.memberType === "primaryMember") {
            ctx.page.primaryMember && (ctx.page.primaryMember[field] = currentList);
        } else {
            ctx.page.partnerMember && (ctx.page.partnerMember[field] = currentList);
        }
    }

    function setContinuedSectionsOnPage(sections: FiduciaryKey[]) {
        if (sections.length === 0) return;
        if (ctx.memberType === "primaryMember") {
            ctx.page.primaryMember && (ctx.page.primaryMember.continuedSections = [...sections]);
        } else {
            ctx.page.partnerMember && (ctx.page.partnerMember.continuedSections = [...sections]);
        }
    }

    while (remainingList.length > 0) {
        if (ctx.pageHeightRef.value + sectionHeight <= TOTAL_PAGE_HEIGHT) {
            // All fit on this page
            const memberOrderGroups = groupMembersByOrder(remainingList);
            const allWithOverride = assignMemberOrderOverride(memberOrderGroups, ctx.memberOrderStartRef.value);
            setFieldOnPage(allWithOverride, allWithOverride.map(m => m.memberOrderOverride!));
            ctx.pageHeightRef.value += sectionHeight;
            remainingList = [];
            setContinuedSectionsOnPage(pendingContinuedSections);
            pendingContinuedSections = [];
        } else {
            // Need to split across pages
            const memberOrderGroups = groupMembersByOrder(remainingList);
            const { fit, rest, memberOrderOverrides } = splitGroupsByHeight(
                memberOrderGroups,
                TOTAL_PAGE_HEIGHT - ctx.pageHeightRef.value,
                ctx.familyTree,
                ctx.memberOrderStartRef.value
            );
            const fitFlat = fit.flat();
            setFieldOnPage(fitFlat, fitFlat.map((_, idx) => memberOrderOverrides[idx]));

            // Only add to continuedSections if some fit and some remain (i.e., split across pages)
            if (fitFlat.length > 0 && rest.flat().length > 0) {
                pendingContinuedSections.push(field);
            }

            pushPage(ctx.page, ctx.memberPages);

            // Prepare next page
            ctx.page = ctx.memberType === "primaryMember"
                ? {primaryMember: blankMember(ctx.blankMemberRef), partnerMember: undefined}
                : {primaryMember: undefined, partnerMember: blankMember(ctx.blankMemberRef)};
            ctx.pageHeightRef.value = SECTION_HEADER_HEIGHT;
            remainingList = rest.flat();
            ctx.memberOrderStartRef.value += fit.length;

            setContinuedSectionsOnPage(pendingContinuedSections);
            pendingContinuedSections = [];
        }
    }
    return {
        page: ctx.page,
        pageHeight: ctx.pageHeightRef.value,
        memberOrderStart: ctx.memberOrderStartRef.value,
        continuedSections: ctx.continuedSectionsRef.value // not used anymore, but kept for compatibility
    };
}

function getMemberPages(
    member: FiduciarySummary["primaryMember"] | undefined,
    memberType: "primaryMember" | "partnerMember",
    blankMemberRef: FiduciarySummary["primaryMember"] | undefined,
    familyTree?: MemberGroup
): FiduciarySummary[] {
    if (!member) return [];

    const memberPages: FiduciarySummary[] = [];
    let page: FiduciarySummary =
        memberType === "primaryMember"
            ? {primaryMember: blankMember(blankMemberRef), partnerMember: undefined}
            : {primaryMember: undefined, partnerMember: blankMember(blankMemberRef)};
    let pageHeight = SECTION_HEADER_HEIGHT;
    let continuedSections: FiduciaryKey[] = [];
    let memberOrderStart = 1;

    const ctx: ProcessFieldContext = {
        memberType,
        blankMemberRef,
        familyTree,
        page,
        memberPages,
        pageHeightRef: { value: pageHeight },
        continuedSectionsRef: { value: continuedSections },
        memberOrderStartRef: { value: memberOrderStart }
    };

    FIDUCIARY_FIELDS.forEach(field => {
        const result = processField(
            field,
            member[field],
            ctx
        );
        ctx.page = result.page;
        ctx.pageHeightRef.value = result.pageHeight;
        ctx.memberOrderStartRef.value = result.memberOrderStart;
        ctx.continuedSectionsRef.value = result.continuedSections;
    });

    pushPage(ctx.page, memberPages);
    return memberPages;
}

/**
 * Splits member groups into those that fit within the given height and those that overflow.
 * Returns:
 * - fit: groups that fit on the current page
 * - rest: groups that overflow to the next page
 * - memberOrderOverrides: override numbers for each group in fit
 */
function splitGroupsByHeight(
    memberOrderGroups: FiduciaryMember[][],
    availableHeight: number,
    memberGroup: MemberGroup | undefined,
    memberOrderStart: number
): {
    fit: FiduciaryMember[][],
    rest: FiduciaryMember[][],
    memberOrderOverrides: number[]
} {
    let usedHeight = 0;
    const fit: FiduciaryMember[][] = [];
    const rest: FiduciaryMember[][] = [];
    const memberOrderOverrides: number[] = [];
    let overrideIndex = memberOrderStart;

    for (const element of memberOrderGroups) {
        const group = element;
        const groupHeight = estimateFiduciaryRowHeight(group, memberGroup);

        if (usedHeight + groupHeight + (fit.length === 0 ? CATEGORY_HEADER_HEIGHT : 0) <= availableHeight) {
            // Add CATEGORY_HEADER_HEIGHT only for the first group
            if (fit.length === 0) {
                usedHeight += CATEGORY_HEADER_HEIGHT;
            }
            usedHeight += groupHeight;
            fit.push(group);
            memberOrderOverrides.push(overrideIndex);
            overrideIndex++;
        } else {
            // Remaining groups go to rest
            rest.push(group);
        }
    }

    return { fit, rest, memberOrderOverrides };
}

export const splitFiduciarySummary = (
    fiduciarySummary: FiduciarySummary,
    familyTree?: MemberGroup
): SplitFiduciarySummaryResult => {
    const primaryPages = getMemberPages(
        fiduciarySummary.primaryMember,
        "primaryMember",
        fiduciarySummary.primaryMember,
        familyTree
    );
    const partnerPages = getMemberPages(
        fiduciarySummary.partnerMember,
        "partnerMember",
        fiduciarySummary.partnerMember,
        familyTree
    );

    const pages: FiduciarySummary[] = [];
    const maxPages = Math.max(primaryPages.length, partnerPages.length);

    for (let i = 0; i < maxPages; i++) {
        const page: FiduciarySummary = {
            primaryMember: primaryPages[i]?.primaryMember,
            partnerMember: partnerPages[i]?.partnerMember
        };
        pushPage(page, pages);
    }

    return {pages};
};
